import { ResourceIdentifier } from "../../ResourceManager/ResourceIdentifier";
import { IWriteOnlyResourceManager, IReadOnlyResourceManager } from "../../ResourceManager/ResourceManager";
import { Resourceful } from "../../ResourceManager/Resourceful";
import { SlotSymbol } from "../SlotSymbol";
import { JSONResource } from "../../ResourceManager/JSONResource";
import { AtlasResource } from "../../ResourceManager/AtlasResource";
import * as PIXI from "pixi.js";
import { CreateElement, CreateSpineResources, Log, LogGroup, Point, Scale, SetStyles, Size } from "../../MyHelpers";
import { ISkeletonData, Spine } from "pixi-spine";
import { SpinnableReelsBigCanvas } from "../SpinAnimation/SpinnableReelsBigCanvas";
import { PixiSpriteResource } from "../../ResourceManager/PixiSpriteResource";
import { SlotSymbolSprite } from "../SlotSymbolSprite";
import { PixiSprite } from "../Sprite/PixiSprite";
import { JackpotDisplayType, Settings } from "../../Settings";
import { children } from "svelte/internal";
import { PlayWindow } from "../../api";
import { SymbolPositionToAnimate } from "../SymbolsAnimation";
import { WinLinesDrawOrder, WinLinesTypeStyle } from "../WinLines/GenericWinLinesAnimation";
import { TransitionPIXITypeStyle } from "../Transition/VideoSpriteTransitionParticlesPIXIFadeInFadeOut";
import { ButtonColors } from "../panel/panel";
import { HelpPanelColor } from "../../help/Help";
import { RewardColor } from "../ScatterRandomAward/RewardOptionFactory";
import { AudioManager, Track } from "../../AudioManager";
import { JackpotDisplayProperties } from "../../JackpotDisplayProperties";

const SymbolsConfig = {
    "10": "Symbol_00",
    "J": "Symbol_01",
    "K": "Symbol_02",
    "Q": "Symbol_03",
    "A": "Symbol_04",
    "S1": "Symbol_05",
    "S2": "Symbol_06",
    "S3": "Symbol_07",
    "S4": "Symbol_08",
    "S5": "Symbol_09",
    "S6": "Symbol_10",
    "FG": "Symbol_11",
    "Wild": "Symbol_12",
    "SRA": "Symbol_SRA",
}

export enum SymbolTypeAnim {
    Sprites = "Sprites",
    Spine = "Spine",
}

interface SymbolNameConfig {
    SymbolName: string
}

interface SymbolSound {
    SymbolName: boolean
}

export enum AnticipationType {
    Spine = "Spine",
    None = "None"
}
export interface GameConfigurationJson {
    bonusMultiplier: boolean,
    symbolsType: SymbolTypeAnim,
    winLinesTypeStyle: WinLinesTypeStyle,
    panelColor: ButtonColors,
    symbolsConfig: SymbolNameConfig[],
    transitionPIXITypeStyle: TransitionPIXITypeStyle,
    backgroundHelpPanelColor: HelpPanelColor,
    colorScatter: RewardColor,
    colorLed: string,
    winLinesDrawOrder: WinLinesDrawOrder,
    poppetPlusPot: boolean,
    symbolSound: SymbolSound[],
    skinPoppet: number,
    symbolScale : Record<string, number>,
    anticipationType: AnticipationType,
    uniReel: boolean,
    enabledPuppet: boolean,
}

export const defaultGameConfig: Partial<GameConfigurationJson> =
{
    bonusMultiplier: true,
    symbolScale :{ 
        
        },
    anticipationType: AnticipationType.Spine
};

export interface SymbolsCollectionResources {
    spineResources?: ISkeletonData,
    imgResource?: HTMLImageElement,
    spritesResources?: PIXI.Texture[],
    showAward: boolean,
}

export interface SymbolsCollectionCache {
    nameSymbol: string,
    symbolContainer: PIXI.AnimatedSprite,
}
export class SymbolCollection implements Resourceful {
    protected symbols: SlotSymbol[] = [];
    protected gameName: string = null;
    protected resid: ResourceIdentifier = null;
    protected gameResorucesPath: string = null;
    protected spinnableReels: SpinnableReelsBigCanvas;
    protected gameConfig: GameConfigurationJson = null;
    protected logGroup = null;
    protected readonly symbolsByJackpot = {
        [JackpotDisplayType.RapidDiamondPot]: ["SRA"],
        [JackpotDisplayType.PlayCityLink]: ["SRA"],
        [JackpotDisplayType.RapidDiamondPotStep]: ["SRA"],
    }

    protected symbolsSpecial: string[] = ["Symbol_FG", "Symbol_SRA", "Symbol_Wild"];
    protected symbolsScatterInsideSpine: string = "SRA";
    protected audio: AudioManager = null;

    protected playWindow : PlayWindow = null;
    constructor(symbols: SlotSymbol[], gameName: string, resid: ResourceIdentifier, gameResorucesPath: string, spinnableReels: SpinnableReelsBigCanvas, gameConfig: GameConfigurationJson, logGroup: LogGroup, playWindow: PlayWindow, audio: AudioManager) {
        this.symbols = symbols;
        ////console.log("InitData:", this.symbols);
        this.gameName = gameName;
        this.resid = resid;
        this.gameResorucesPath = gameResorucesPath;
        this.spinnableReels = spinnableReels;
        this.gameConfig = gameConfig;
        this.logGroup = logGroup;

        this.playWindow = playWindow;
        this.audio = audio;

        for (let reel = 0; reel < playWindow.Width; reel++) {
            this.symbolCollectionDrawSaved.push([]);
            this.cacheSymbolMap.push([])
            for (let row = 0; row < playWindow.Height; row++) {
                this.symbolCollectionDrawSaved[reel][row] = null;
                this.cacheSymbolMap[reel][row] = { nameSymbol: null, symbolContainer: null };
            }
        }
    }

    public async PrepareResources(resourceManager: IWriteOnlyResourceManager) {
        await this.PrepareSprite(resourceManager);
        await this.PrepareSpine(resourceManager);
        //console.log(`SymbolsCollection-GameConfig-${this.gameName}:`, this.gameConfig.symbolsType);
    }

    protected async PrepareSpine(resourceManager: IWriteOnlyResourceManager) {
        if (this.gameConfig.symbolsType != SymbolTypeAnim.Spine) {
            return;
        }

        //this.symbols.forEach(element => {
        for (const element of this.symbols) {
            let symbol = this.gameConfig.symbolsConfig[element.ID]
            if (symbol == null) {
                throw new Error(`Symbol ${element.ID} not found in symbol collection`);
            }

            //console.log(`SymbolToLoad-${this.gameName}-${element.ID}:`, SymbolsConfig[element.ID]);
            resourceManager.Add(new JSONResource(`${this.gameResorucesPath}resources/SymbolsSpine/${symbol}.json`, this.resid.ScopedResourceName(`Symbol-${element.ID}-JSON`)));
            resourceManager.Add(new AtlasResource(`${this.gameResorucesPath}resources/SymbolsSpine/${symbol}.atlas`, this.resid.ScopedResourceName(`Symbol-${element.ID}-ATLAS`)));

            let symbolsSound = this.gameConfig.symbolSound?.[element.ID] as boolean
            if (symbolsSound != null && symbolsSound == true) this.audio.AddTrack(new Track(`${this.gameResorucesPath}resources/SymbolsSpine/tracks/${element.ID}` + Settings.audioFormat, this.resid.ScopedResourceName(`Symbol_${element.ID}-SoundTrack`), true))
        };
    }

    protected async PrepareSprite(resourceManager: IWriteOnlyResourceManager) {
        
        const jackpotAlias = JackpotDisplayProperties[Settings.JackpotDisplayType]?.alias ?? Settings.JackpotDisplayType;
        

        if (this.gameConfig.symbolsType == SymbolTypeAnim.Sprites) {
            for (let s of this.symbols) {

                let symbolName: string = s.ID;

                let symbol = this.gameConfig.symbolsConfig[s.ID]
                if (symbol != null) {

                    let symbolPath = this.gameResorucesPath + "resources/symbols2/" + symbol;

                    if (this.symbolsByJackpot[jackpotAlias]?.includes(symbolName)) {
                        symbolPath = this.gameResorucesPath + "resources/SymbolsJackpots/" + jackpotAlias + "/" + "symbol_" + symbolName;
                    }

                    resourceManager.AddGlobal(new PixiSpriteResource(symbolPath, this.resid.Add("Symbol").Add(s.ID).Shared(), true, this.logGroup.Add("Symbol").Add(s.ID), true));

                }
                ////console.log("Symbols-Sprites-ResourcesIdentifier-PrepareResources:", this.resid.Add("Symbol").Add(s.ID).Scope());
            }
            ////console.log(`SymbolsCollection-GameConfig-${this.gameName}:`,this.gameConfig.symbolsType);
        }
    }

    public symbolCollectionMap: Map<string, SymbolsCollectionResources> = new Map<string, SymbolsCollectionResources>();
    public async ResourcesLoaded(resourceManager: IReadOnlyResourceManager) {
        await this.ResourcesLoadedSprite(resourceManager);
        await this.ResourcesLoadedSpine(resourceManager);
        //console.log(`SymbolsCollectionMap-${this.gameName}:`, this.symbolCollectionMap);
    }

    protected async ResourcesLoadedSpine(resourceManager: IReadOnlyResourceManager) {
        if (this.gameConfig.symbolsType != SymbolTypeAnim.Spine) {
            // no spine, nothing to load
            return;
        }

        for (let element of this.symbols) {
            let symbol = this.gameConfig.symbolsConfig[element.ID];
            if (symbol == null) {
                // missing symbol?
                throw new Error(`Symbol ${element.ID} not found in symbol collection`);
            }

            let symbolJsonResource = resourceManager.Get<JSONResource>(this.resid.ScopedResourceName(`Symbol-${element.ID}-JSON`));
            let pixiAssetSymbol = await PIXI.Assets.load(symbolJsonResource.path);
            PIXI.Assets.cache.remove(symbolJsonResource.path);

            let skeleton = pixiAssetSymbol.spineData as ISkeletonData;


            if (element.ID == this.symbolsScatterInsideSpine) {
                let bone = skeleton.bones.find(bone => bone.name == "value");
                if (!bone) {
                    throw new Error(`Symbol-${element.ID} Check resources its not the last version , its Missing the bone for and ScatterRandomAward  into Spine - ${this.gameName}`);
                }
            }




            let spine = await CreateSpineResources(pixiAssetSymbol.spineData, false);
            // spine.cacheAsBitmap = true;
            // spine.cacheAsBitmapMultisample = PIXI.MSAA_QUALITY.NONE;
            // spine.cacheAsBitmapResolution = 1;

            let showAward = true;
            if (spine.skeleton.findBone("hideAward")) {
                showAward = false;
            }

            //spine.width = this.spinnableReels.GetSymbolSize().width;
            //spine.height = this.spinnableReels.GetSymbolSize().height;
            //spine.position.set(0, 0);
            //spine.update(0);
            const spineCorrectedWidth = spine.spineData.width;
            const spineCorrectedHeight = spine.spineData.height;

            let renderTexture = PIXI.RenderTexture.create({ width: spineCorrectedWidth, height: spineCorrectedHeight });
            // Render Spine object onto render texture
            let renderer = new PIXI.Renderer({
                width: spineCorrectedWidth, 
                height: spineCorrectedHeight
            });
            renderer.render(spine, { renderTexture: renderTexture });
            // Create a new Sprite using the RenderTexture                
            // Extract the canvas                
            //let canvas = renderer.plugins.extract.canvas(new PIXI.Sprite(renderTexture)) as HTMLCanvasElement;

            //let img = renderer.plugins.extract.img()
            // //create img div empty

            let imgConverted = CreateElement("img", {
                style: {
                    width: spineCorrectedWidth + "px",
                    height: spineCorrectedHeight + "px",
                    backgroundPosition: "center",
                    backgroundSize: "contain",
                    backgroundOrigin: "center",
                }
            });
            //add img src from data img saved 
            imgConverted.src = (renderer.plugins.extract.canvas(new PIXI.Sprite(renderTexture)) as HTMLCanvasElement).toDataURL("image/png", 1);


            

            let scaleWidth = this.spinnableReels?.GetSymbolSize().Width / spineCorrectedWidth;
            let scaleHeight = this.spinnableReels?.GetSymbolSize().Height / spineCorrectedHeight;
            let scaleProm = (scaleHeight + scaleWidth) / 2;
            imgConverted.width = spineCorrectedWidth * scaleProm;
            imgConverted.height = spineCorrectedHeight * scaleProm;

            this.symbolCollectionMap.set("Symbol_" + element.ID, {
                spineResources: pixiAssetSymbol.spineData,
                imgResource: imgConverted,
                showAward: showAward,
            });


            spine.autoUpdate = false;

            //try {
            renderer.clear();
            renderer.destroy(true);
            renderTexture.destroy(true);
            //spine.autoUpdate = false;
            spine.destroy({children: true, texture: false, baseTexture: true});
            spine = null;
            //imgConverted = null;
            // } catch (error) {
            //     Log.Error(this.logGroup, "Error destroying spine for symbol " + element.ID + " with index " + element.Index);
            // }
        }
    }

    //public pixiSpritesByName: Map<string, PIXI.Texture[]> = new Map<string, PIXI.Texture[]>();
    //public symbolSpriteAnims: SlotSymbolSprite[] = [];
    //public symbolSpriteAnimsByName: Map<string, SlotSymbolSprite> = new Map<string, SlotSymbolSprite>();

    protected async ResourcesLoadedSprite(resourceManager: IReadOnlyResourceManager) {
        if (this.gameConfig.symbolsType == SymbolTypeAnim.Sprites) {

            for (const element of this.symbols) {
                //let symbol = SymbolsConfig[element.ID];
                //let symbolSprite: SlotSymbolSprite;
                //try {
                ////console.log("Symbols-Sprites-ResourcesIdentifier-ResourcesLoaded:", this.resid.Add("Symbol").Add(element.ID).Shared());

                let sprite: PixiSprite = await resourceManager.Get<PixiSpriteResource>(this.resid.Add("Symbol").Add(element.ID).Shared()).sprite
                let symbolSprite = new SlotSymbolSprite(
                    this.spinnableReels.GetSymbolSize(),
                    sprite,
                    element.Index,
                );
                let Textures: PIXI.Texture[] = [];

                for (let decodedFrame of sprite.DecodedFrames) {
                    ////console.log(`Symbol_FrameDecode ${s.ID}`, decodedFrames);

                    let symbolTexture = PIXI.Texture.from(decodedFrame);
                    let scaleWidth = this.spinnableReels?.GetSymbolSize().Width / symbolTexture.width;
                    let scaleHeight = this.spinnableReels?.GetSymbolSize().Height / symbolTexture.height
                    let scaleProm = (scaleHeight + scaleWidth) / 2;
                    symbolTexture.baseTexture.width = symbolTexture.width * scaleProm;
                    symbolTexture.baseTexture.height = symbolTexture.height * scaleProm
                    
                    Textures.push(symbolTexture);
                    ////console.log(`Symbol-Frame-${element.ID}:`, Textures);
                }

                //this.pixiSpritesByName.set(`Symbol_${element.ID}`, Textures);                


                //Create img 
                let canvas = document.createElement('canvas');
                let ctx = canvas.getContext('2d');
                canvas.width = sprite.IdleFrame.width;
                canvas.height = sprite.IdleFrame.height;
                ctx.drawImage(sprite.IdleFrame, 0, 0);
                let dataUrl = canvas.toDataURL("image/png", 1.0);
                let imgConverted = new Image();
                imgConverted.src = dataUrl;




                SetStyles(imgConverted, {
                    width: sprite.IdleFrame.width + "px",
                    height: sprite.IdleFrame.height + "px",
                    backgroundPosition: "center",
                    backgroundSize: "contain",
                    backgroundOrigin: "center",
                })

                let scaleWidth = imgConverted.width / this.spinnableReels?.GetSymbolSize().Width;
                let scaleHeight = imgConverted.height / this.spinnableReels?.GetSymbolSize().Height;
                let scaleProm = (scaleHeight + scaleWidth) / 2;
                imgConverted.width = imgConverted.width * scaleProm;
                imgConverted.height = imgConverted.height * scaleProm;

                this.symbolCollectionMap.set("Symbol_" + element.ID, {
                    spritesResources: Textures,
                    imgResource: imgConverted,
                    showAward: true,
                });

                // }
                // catch {
                //     Log.Error(this.logGroup, "SymbolsCollection Unable to load SPRITE for symbol " + element.ID + " with index " + element.Index);
                // }

                //this.symbolSpriteAnims.push(symbolSprite);
                //this.symbolSpriteAnimsByName.set(element.ID, symbolSprite);
            };
        }
        ////console.log(`SymbolCollectionMapSprites-${this.gameName}:`, this.symbolCollectionMap);
    }

    //protected symbolCollectionDebugger: SymbolsCollectionDebuggerSpine[] = [];   
    protected symbolCollectionDrawSaved: Spine[][] = [];
    protected offsetX: number = 9
    
    //funtion for create spine , need to send the json file 
    public CreateSpineResources(nameSymbol: string, symbolSize: Size, reelIndex: number, rowIndex: number, positonPoint: Point): Spine {
        this.DestroySymbol(reelIndex, rowIndex);
        let spine = CreateSpineResources(this.symbolCollectionMap.get(nameSymbol).spineResources, false);
        // const localBounds = spine.getLocalBounds();
        // const spineScale = 1;
        const scalePercentage = !this.gameConfig?.symbolScale ? 1 : this.gameConfig.symbolScale[`${this.playWindow.Width}x${this.playWindow.Height}`] ;

        const spineCorrectedWidth = spine.spineData.width;
        const spineCorrectedHeight = spine.spineData.height;

        let scaleWidth = (symbolSize.Width / spineCorrectedWidth) * scalePercentage;
        let scaleHeight = (symbolSize.Height / spineCorrectedHeight) * scalePercentage;
        let scaleProm = (scaleHeight + scaleWidth) / 2;       
        //spine.scale.set(scaleProm);
        spine.pivot.set(spineCorrectedWidth / 2, spineCorrectedHeight / 2); 
        //spine.width = spine.width * scale;
        //spine.height = spine.height * scale;
        spine.scale.set(scaleProm);
        spine.state.timeScale = this.speedAnimation;



        spine.position.set(positonPoint.x, positonPoint.y);

        spine.name = nameSymbol;


        this.symbolCollectionDrawSaved[reelIndex][rowIndex] = spine;
        if (this.symbolCollectionDrawSaved[reelIndex][rowIndex]?.state?.hasAnimation("idle") && this.symbolsSpecial.find(symbol => symbol == this.symbolCollectionDrawSaved[reelIndex][rowIndex].name))
        {
            this.symbolCollectionDrawSaved[reelIndex][rowIndex]?.state?.setAnimation(0, "idle", true);
        }
        //console.log("SymbolsCollection:",this.symbolCollectionDebugger);
        return spine;
    }

    public DestroySymbol(reelIndex: number, rowIndex: number) {
        this.symbolCollectionDrawSaved[reelIndex][rowIndex]?.destroy({children: true, texture: false, baseTexture: true});
        this.symbolCollectionDrawSaved[reelIndex][rowIndex] = null
    }

    protected CheckIfSpineExist(reelIndex: number, rowIndex: number, nameSymbol: string) {
        this.symbolCollectionDrawSaved[reelIndex][rowIndex]?.name == nameSymbol
    }

    public PlayAnimationWinSymbolSpineSymbol(symbolPositions: SymbolPositionToAnimate[], loop: boolean = false) {
        //get symbols for anim
        if (this.gameConfig.symbolsType == SymbolTypeAnim.Spine)
            symbolPositions?.forEach((s) => {
                //this.symbolPIXISprite?.[s.reel]?.[s.row]?.play();
                //this.symbolCollectionDrawSaved[s.reel][s.row]?.state?.clearTrack(1);          
                this.audio.Play(this.resid.ScopedResourceName(`${this.symbolCollectionDrawSaved[s.reel][s.row]?.name}-SoundTrack`))
                if (this.symbolCollectionDrawSaved[s.reel][s.row]?.state?.hasAnimation("winSymbol")) this.symbolCollectionDrawSaved[s.reel][s.row]?.state?.setAnimation(0, "winSymbol", loop);
            });
    }

    public PlayAnimationIdleSpineSymbol() {
        for (let reel = 0; reel < this.symbolCollectionDrawSaved.length; reel++) {
            for (let row = 0; row < this.symbolCollectionDrawSaved[reel].length; row++) {

                if (this.symbolCollectionDrawSaved[reel][row]?.state?.hasAnimation("idle") && this.symbolsSpecial.find(symbol => symbol == this.symbolCollectionDrawSaved[reel][row].name)) {
                    this.symbolCollectionDrawSaved[reel][row]?.state?.setAnimation(0, "idle", true);
                }
            }
        }
    }

    public PlayAnimationReelStopSpineSymbol(reelIndex: number, rowIndex: number) {
        if (this.gameConfig.symbolsType == SymbolTypeAnim.Spine)
            if (this.symbolCollectionDrawSaved[reelIndex][rowIndex]?.state?.hasAnimation("reelStop") && this.symbolsSpecial.find(symbol => symbol == this.symbolCollectionDrawSaved[reelIndex][rowIndex].name)) this.symbolCollectionDrawSaved[reelIndex][rowIndex]?.state?.setAnimation(1, "reelStop", false);
    }

    public DestroyAllSymbols() {
        this.symbolCollectionDrawSaved = [];
        this.cacheSymbolMap = []
        for (let reel = 0; reel < this.playWindow.Width; reel++) {
            this.symbolCollectionDrawSaved.push([]);
            this.cacheSymbolMap.push([]);
            for (let row = 0; row < this.playWindow.Height; row++) {
                this.symbolCollectionDrawSaved[reel][row] = null;
                this.cacheSymbolMap[reel][row] = { nameSymbol: null, symbolContainer: null };
            }
        }
    }

    protected speedAnimation: number = 1
    public SetSymbolAnimationSpeed(speedAnimation: number) {
        this.speedAnimation = speedAnimation;
    }

    protected cacheSymbolMap: SymbolsCollectionCache[][] = []
    public SetSymbolCache(nameSymbol: string, reelIndex: number, rowIndex: number, symbolContainer: PIXI.AnimatedSprite) {
        this.cacheSymbolMap[reelIndex][rowIndex].nameSymbol = nameSymbol;
        this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer = symbolContainer;
        //console.log("CacheSymbol",this.cacheSymbolMap);
    }


    public CreateSymbolAnimation(reelIndex: number,) {
        for (let rowIndex: number = 0; rowIndex < this.cacheSymbolMap[reelIndex].length; rowIndex++) {
            if (this.gameConfig.symbolsType == SymbolTypeAnim.Spine) {
                //this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer.visible = false ;
                //this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer.scale.set(1); 
                if (this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer.parent) {
                    this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer.parent.addChild(this.CreateSpineResources(this.cacheSymbolMap[reelIndex][rowIndex].nameSymbol, this.spinnableReels.GetSymbolSize(), reelIndex, rowIndex, new Point(this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer.x, this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer.y)));
                    this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer.texture = PIXI.Texture.EMPTY;
                }
            } else if (this.gameConfig.symbolsType == SymbolTypeAnim.Sprites) {
                this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer.textures = this.symbolCollectionMap.get(this.cacheSymbolMap[reelIndex][rowIndex].nameSymbol).spritesResources;
                //scale texture its changed by code use scaleRatio 1 : 1 
                this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer.visible = true;
                //this.cacheSymbolMap[reelIndex][rowIndex].symbolContainer.scale.set(1);
            }
        }
    }

    public CreateSymbolAnimationAllReels() {
        for (let reelIndex: number = 0; reelIndex < this.cacheSymbolMap.length; reelIndex++) {
            this.CreateSymbolAnimation(reelIndex);
        }
    }
}
